import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Eye,
  Factory,
  Loader2,
  Plus,
  RefreshCw,
  Edit,
  Search,
  PackageCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { formatDate } from '../utils/dateUtils';
import {
  jobWorkApiService,
  JobWorkOrderDto,
  JobWorkOrderStatus,
  JobWorkPendingDto,
  JOB_WORK_ORDER_STATUSES,
  isJobWorkEditable,
  isJobWorkOpenForReceive,
} from '../services/jobWorkApi';
import { PermissionGate } from './ui/PermissionGate';

const JOBWORK_ORDERS_ROUTE = '/jobwork/orders';

/** Pending send-challan capacity (what JW can still receive against). */
type ReceiveLineDraft = {
  key: string;
  sendChallanId: number;
  sendChallanNo?: string | null;
  sendLineId: number;
  itemLabel: string;
  jobType?: string | null;
  openQty: number;
  sentQty: number;
  alreadyReceived: number;
  receiveQty: string;
  scrapQty: string;
  orderOutputLineId?: number | null;
};

/** Per linked WO receive entry — summed into JW receive qty on confirm. */
type ReceiveWoDraft = {
  key: string;
  workOrderId: number;
  workOrderNumber: string;
  linkedQty: number;
  openQty: number;
  receiveQty: string;
  scrapQty: string;
};

function statusBadgeClass(status?: string): string {
  switch (String(status ?? '').toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-800 border-gray-300';
    case 'APPROVED':
    case 'RELEASED':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'PARTIAL':
      return 'bg-amber-100 text-amber-800 border-amber-300';
    case 'COMPLETED':
    case 'CLOSED':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'CANCELLED':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
}

function formatQty(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Distribute total qty across send lines by remaining open capacity. */
function allocateAcrossLines(
  lines: ReceiveLineDraft[],
  totalQty: number,
  totalScrap: number
): Array<{
  sendChallanId: number;
  sendLineId: number;
  qty: number;
  scrapQty: number;
  orderOutputLineId?: number | null;
}> {
  const payload: Array<{
    sendChallanId: number;
    sendLineId: number;
    qty: number;
    scrapQty: number;
    orderOutputLineId?: number | null;
  }> = [];
  let qtyLeft = totalQty;
  let scrapLeft = totalScrap;

  for (const line of lines) {
    if (qtyLeft <= 0) break;
    const capacity = Math.max(0, line.openQty);
    if (capacity <= 0) continue;

    const takeQty = Math.min(capacity, qtyLeft);
    const remAfterQty = capacity - takeQty;
    const takeScrap = Math.min(remAfterQty, scrapLeft);

    if (takeQty > 0) {
      payload.push({
        sendChallanId: line.sendChallanId,
        sendLineId: line.sendLineId,
        qty: takeQty,
        scrapQty: takeScrap,
        orderOutputLineId: line.orderOutputLineId ?? null,
      });
      scrapLeft -= takeScrap;
    }
    qtyLeft -= takeQty;
  }

  // Attach any leftover scrap to the last receive line (still within open capacity).
  if (scrapLeft > 0 && payload.length > 0) {
    const last = payload[payload.length - 1];
    const source = lines.find((l) => l.sendLineId === last.sendLineId);
    const maxScrap = Math.max(0, (source?.openQty ?? 0) - last.qty - last.scrapQty);
    last.scrapQty += Math.min(maxScrap, scrapLeft);
  }

  return payload.filter((p) => p.qty > 0);
}

export function JobWorkOrderListingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [orders, setOrders] = useState<JobWorkOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveOrder, setReceiveOrder] = useState<JobWorkOrderDto | null>(null);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [receiveLines, setReceiveLines] = useState<ReceiveLineDraft[]>([]);
  const [receiveWoLines, setReceiveWoLines] = useState<ReceiveWoDraft[]>([]);

  const branchId = selectedBranch ?? user?.defaultBranchId ?? null;

  const receiveByWo = receiveWoLines.length > 1;

  const totalOpenQty = useMemo(
    () => receiveLines.reduce((s, l) => s + (Number(l.openQty) || 0), 0),
    [receiveLines]
  );

  const woReceiveSum = useMemo(() => {
    let qty = 0;
    let scrap = 0;
    for (const wo of receiveWoLines) {
      qty += Number(wo.receiveQty) || 0;
      scrap += Number(wo.scrapQty) || 0;
    }
    return { qty, scrap, total: qty + scrap };
  }, [receiveWoLines]);

  const loadOrders = useCallback(async () => {
    if (branchId == null) {
      setOrders([]);
      return;
    }
    try {
      setLoading(true);
      const status = statusFilter === 'ALL' ? undefined : statusFilter;
      const res = await jobWorkApiService.listOrders(branchId, status);
      setOrders(res.success && Array.isArray(res.data) ? res.data : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, statusFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const filteredOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const no = (o.jwOrderNo ?? '').toLowerCase();
      const notes = (o.notes ?? '').toLowerCase();
      const party = (o.partyType ?? '').toLowerCase();
      const supplier = (o.supplierName ?? '').toLowerCase();
      const toBranch = (o.toBranchName ?? '').toLowerCase();
      const endgood = (o.endgoodName ?? '').toLowerCase();
      const process = (o.processName ?? '').toLowerCase();
      return (
        no.includes(q) ||
        notes.includes(q) ||
        party.includes(q) ||
        supplier.includes(q) ||
        toBranch.includes(q) ||
        endgood.includes(q) ||
        process.includes(q) ||
        String(o.id).includes(q)
      );
    });
  }, [orders, searchTerm]);

  const openReceiveDialog = async (order: JobWorkOrderDto) => {
    if (order.id == null) return;
    setReceiveOrder(order);
    setReceiveOpen(true);
    setReceiveLines([]);
    setReceiveWoLines([]);
    setReceiveLoading(true);
    try {
      const [pendingRes, orderRes] = await Promise.all([
        jobWorkApiService.getPending(branchId),
        jobWorkApiService.getOrderById(order.id),
      ]);
      const fullOrder = orderRes.success && orderRes.data ? orderRes.data : order;
      setReceiveOrder(fullOrder);

      const defaultOutputLineId =
        fullOrder.outputLines?.find((l) => l.id != null)?.id ?? null;

      const pendingForOrder = (pendingRes.data ?? []).filter(
        (p: JobWorkPendingDto) => p.jobWorkOrderId === order.id
      );

      const drafts: ReceiveLineDraft[] = [];
      for (const pending of pendingForOrder) {
        for (const line of pending.lines ?? []) {
          const openQty = Number(line.openQty) || 0;
          if (openQty <= 0 || line.sendLineId == null) continue;
          drafts.push({
            key: `${pending.sendChallanId}-${line.sendLineId}`,
            sendChallanId: pending.sendChallanId,
            sendChallanNo: pending.sendChallanNo,
            sendLineId: line.sendLineId,
            itemLabel:
              line.endgoodName ||
              line.rawItemName ||
              (line.endgoodId != null
                ? `EG #${line.endgoodId}`
                : line.rawItemId != null
                  ? `Raw #${line.rawItemId}`
                  : `Line #${line.sendLineId}`),
            jobType: line.jobType,
            openQty,
            sentQty: Number(line.sentQty) || 0,
            alreadyReceived: Number(line.receivedQty) || 0,
            receiveQty: '',
            scrapQty: '0',
            orderOutputLineId: defaultOutputLineId,
          });
        }
      }
      setReceiveLines(drafts);

      const totalOpen = drafts.reduce((s, l) => s + l.openQty, 0);
      const linkedWos = (fullOrder.workOrders ?? []).filter((wo) => wo.workOrderId != null);
      const totalLinked = linkedWos.reduce((s, wo) => s + (Number(wo.qty) || 0), 0);

      if (linkedWos.length > 1 && totalOpen > 0) {
        const woDrafts: ReceiveWoDraft[] = linkedWos.map((wo) => {
          const linkedQty = Number(wo.qty) || 0;
          const share = totalLinked > 0 ? linkedQty / totalLinked : 0;
          const openQty = Math.round(share * totalOpen * 1000) / 1000;
          return {
            key: `wo-${wo.workOrderId}`,
            workOrderId: wo.workOrderId,
            workOrderNumber: wo.workOrderNumber || `WO #${wo.workOrderId}`,
            linkedQty,
            openQty,
            receiveQty: '',
            scrapQty: '0',
          };
        });
        // Fix rounding so WO opens sum to totalOpen
        const woOpenSum = woDrafts.reduce((s, w) => s + w.openQty, 0);
        const drift = Math.round((totalOpen - woOpenSum) * 1000) / 1000;
        if (woDrafts.length > 0 && Math.abs(drift) >= 0.001) {
          const last = woDrafts[woDrafts.length - 1];
          last.openQty = Math.max(0, Math.round((last.openQty + drift) * 1000) / 1000);
        }
        setReceiveWoLines(woDrafts);
      } else {
        setReceiveWoLines([]);
      }

      if (drafts.length === 0) {
        toast.error('No open material to receive for this order');
      }
    } catch {
      setReceiveLines([]);
      setReceiveWoLines([]);
    } finally {
      setReceiveLoading(false);
    }
  };

  const updateReceiveLine = (key: string, field: 'receiveQty' | 'scrapQty', value: string) => {
    setReceiveLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, [field]: value } : line))
    );
  };

  const updateReceiveWo = (key: string, field: 'receiveQty' | 'scrapQty', value: string) => {
    setReceiveWoLines((prev) =>
      prev.map((wo) => (wo.key === key ? { ...wo, [field]: value } : wo))
    );
  };

  const fillAllOpen = () => {
    if (receiveByWo) {
      setReceiveWoLines((prev) =>
        prev.map((wo) => ({ ...wo, receiveQty: String(wo.openQty), scrapQty: '0' }))
      );
    } else {
      setReceiveLines((prev) =>
        prev.map((line) => ({ ...line, receiveQty: String(line.openQty), scrapQty: '0' }))
      );
    }
  };

  const clearReceiveQtys = () => {
    if (receiveByWo) {
      setReceiveWoLines((prev) => prev.map((wo) => ({ ...wo, receiveQty: '', scrapQty: '0' })));
    } else {
      setReceiveLines((prev) => prev.map((line) => ({ ...line, receiveQty: '', scrapQty: '0' })));
    }
  };

  const handleConfirmReceive = async () => {
    if (!receiveOrder?.id) return;

    let payload: Array<{
      sendChallanId: number;
      sendLineId: number;
      qty: number;
      scrapQty: number;
      orderOutputLineId?: number | null;
    }> = [];

    if (receiveByWo) {
      for (const wo of receiveWoLines) {
        const qty = Number(wo.receiveQty);
        const scrap = Number(wo.scrapQty) || 0;
        if (!wo.receiveQty.trim() && !wo.scrapQty.trim()) continue;
        if (Number.isNaN(qty) || qty < 0 || Number.isNaN(scrap) || scrap < 0) {
          toast.error(`Invalid qty for ${wo.workOrderNumber}`);
          return;
        }
        if (qty + scrap > wo.openQty + 1e-9) {
          toast.error(
            `Receive+scrap (${qty + scrap}) exceeds open (${wo.openQty}) for ${wo.workOrderNumber}`
          );
          return;
        }
      }

      const totalQty = receiveWoLines.reduce((s, wo) => s + (Number(wo.receiveQty) || 0), 0);
      const totalScrap = receiveWoLines.reduce((s, wo) => s + (Number(wo.scrapQty) || 0), 0);
      if (totalQty <= 0) {
        toast.error('Enter receive qty for at least one work order');
        return;
      }
      if (totalQty + totalScrap > totalOpenQty + 1e-9) {
        toast.error(
          `Total receive+scrap (${totalQty + totalScrap}) exceeds JW open qty (${totalOpenQty})`
        );
        return;
      }

      payload = allocateAcrossLines(receiveLines, totalQty, totalScrap);
      if (payload.length === 0) {
        toast.error('Could not allocate receive qty to send lines');
        return;
      }
    } else {
      for (const line of receiveLines) {
        const qty = Number(line.receiveQty);
        const scrap = Number(line.scrapQty) || 0;
        if (!line.receiveQty.trim()) continue;
        if (Number.isNaN(qty) || qty < 0) {
          toast.error(`Invalid receive qty for ${line.itemLabel}`);
          return;
        }
        if (qty + scrap > line.openQty + 1e-9) {
          toast.error(
            `Receive+scrap (${qty + scrap}) exceeds open qty (${line.openQty}) for ${line.itemLabel}`
          );
          return;
        }
      }

      payload = receiveLines
        .map((line) => ({
          sendChallanId: line.sendChallanId,
          sendLineId: line.sendLineId,
          qty: Number(line.receiveQty) || 0,
          scrapQty: Number(line.scrapQty) || 0,
          orderOutputLineId: line.orderOutputLineId ?? null,
        }))
        .filter((l) => l.qty > 0);

      if (payload.length === 0) {
        toast.error('Enter receive qty for at least one line');
        return;
      }
    }

    try {
      setReceiveSaving(true);
      const res = await jobWorkApiService.receiveAgainstPending(
        receiveOrder.id,
        payload,
        user?.id
      );
      if (res.success) {
        setReceiveOpen(false);
        setReceiveOrder(null);
        setReceiveLines([]);
        setReceiveWoLines([]);
        await loadOrders();
      }
    } catch {
      // Backend error already toasted by API service
    } finally {
      setReceiveSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <Factory className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Job Work Orders</CardTitle>
                  <p className="text-sm text-primary-100">Draft → Approve → Receive</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => void loadOrders()}
                  disabled={loading}
                  className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <PermissionGate route={JOBWORK_ORDERS_ROUTE} action="create">
                  <Button
                    onClick={() => navigate('/jobwork/orders/new')}
                    className="bg-white text-primary-700 hover:bg-primary-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New JW Order
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="jwSearch">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="jwSearch"
                    className="pl-9"
                    placeholder="JW no, endgood, process, party, notes…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All</SelectItem>
                    {JOB_WORK_ORDER_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardContent className="p-6">
            {branchId == null ? (
              <div className="text-center py-10 text-gray-500">Select a branch to view job work orders.</div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No job work orders found.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>JW Order No</TableHead>
                      <TableHead>Order Date</TableHead>
                      <TableHead>Endgood</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Party</TableHead>
                      <TableHead>Expected Return</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issue Lines</TableHead>
                      <TableHead>Output Lines</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => {
                      const status = (order.status ?? 'DRAFT') as JobWorkOrderStatus;
                      const canEdit = isJobWorkEditable(status);
                      const canReceive = isJobWorkOpenForReceive(status);
                      return (
                        <TableRow key={order.id} className="hover:bg-primary-50/40">
                          <TableCell className="font-medium">{order.jwOrderNo || `#${order.id}`}</TableCell>
                          <TableCell>{order.orderDate ? formatDate(order.orderDate) : '—'}</TableCell>
                          <TableCell>
                            {order.endgoodName ||
                              (order.endgoodId != null ? `#${order.endgoodId}` : '—')}
                          </TableCell>
                          <TableCell>
                            {order.processName ||
                              (order.processId != null ? `#${order.processId}` : '—')}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{order.partyType}</div>
                              <div className="text-gray-500 text-xs">
                                {order.partyType === 'VENDOR'
                                  ? order.supplierName ||
                                    (order.supplierId != null
                                      ? `Supplier #${order.supplierId}`
                                      : '—')
                                  : order.toBranchName ||
                                    (order.toBranchId != null
                                      ? `Branch #${order.toBranchId}`
                                      : '—')}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {order.expectedReturnDate ? formatDate(order.expectedReturnDate) : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusBadgeClass(status)}>
                              {status}
                            </Badge>
                          </TableCell>
                          <TableCell>{order.issueLines?.length ?? 0}</TableCell>
                          <TableCell>{order.outputLines?.length ?? 0}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/jobwork/orders/${order.id}/view`)}
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {canEdit ? (
                                <PermissionGate route={JOBWORK_ORDERS_ROUTE} action="edit">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/jobwork/orders/${order.id}/edit`)}
                                    title="Edit"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </PermissionGate>
                              ) : null}
                              {canReceive ? (
                                <PermissionGate route={JOBWORK_ORDERS_ROUTE} action="edit">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-primary-700 border-primary-200 hover:bg-primary-50"
                                    onClick={() => void openReceiveDialog(order)}
                                    title="Receive material"
                                  >
                                    <PackageCheck className="h-4 w-4 mr-1" />
                                    Receive
                                  </Button>
                                </PermissionGate>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={receiveOpen}
        onOpenChange={(open) => {
          if (!receiveSaving) {
            setReceiveOpen(open);
            if (!open) {
              setReceiveOrder(null);
              setReceiveLines([]);
              setReceiveWoLines([]);
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive material</DialogTitle>
            <DialogDescription>
              {receiveOrder?.jwOrderNo || (receiveOrder?.id != null ? `JW #${receiveOrder.id}` : '')}
              {receiveByWo
                ? ' — enter receive qty per work order (partial allowed). Totals are summed to update the JW.'
                : ' — enter qty to receive (partial allowed). Full receive → COMPLETED; partial → PARTIAL.'}
            </DialogDescription>
          </DialogHeader>

          {receiveLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : receiveLines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No open material pending receive for this order.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  JW open qty: <span className="font-medium text-foreground">{formatQty(totalOpenQty)}</span>
                  {receiveByWo ? (
                    <>
                      {' · '}WO receive sum:{' '}
                      <span className="font-medium text-foreground">{formatQty(woReceiveSum.qty)}</span>
                      {woReceiveSum.scrap > 0 ? (
                        <>
                          {' · '}scrap:{' '}
                          <span className="font-medium text-foreground">
                            {formatQty(woReceiveSum.scrap)}
                          </span>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={fillAllOpen}>
                    Fill all open
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={clearReceiveQtys}>
                    Clear
                  </Button>
                </div>
              </div>

              {receiveByWo ? (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Work order</TableHead>
                        <TableHead className="text-right">JW linked qty</TableHead>
                        <TableHead className="text-right">Open share</TableHead>
                        <TableHead className="text-right w-28">Receive qty</TableHead>
                        <TableHead className="text-right w-24">Scrap</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receiveWoLines.map((wo) => (
                        <TableRow key={wo.key}>
                          <TableCell className="font-medium">{wo.workOrderNumber}</TableCell>
                          <TableCell className="text-right">{formatQty(wo.linkedQty)}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatQty(wo.openQty)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={0}
                              max={wo.openQty}
                              step="any"
                              className="h-8 text-right"
                              value={wo.receiveQty}
                              onChange={(e) =>
                                updateReceiveWo(wo.key, 'receiveQty', e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={0}
                              step="any"
                              className="h-8 text-right"
                              value={wo.scrapQty}
                              onChange={(e) =>
                                updateReceiveWo(wo.key, 'scrapQty', e.target.value)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/40 font-medium">
                        <TableCell>Total (to JW)</TableCell>
                        <TableCell className="text-right">
                          {formatQty(
                            receiveWoLines.reduce((s, w) => s + (Number(w.linkedQty) || 0), 0)
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatQty(totalOpenQty)}</TableCell>
                        <TableCell className="text-right">{formatQty(woReceiveSum.qty)}</TableCell>
                        <TableCell className="text-right">{formatQty(woReceiveSum.scrap)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Send challan</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Sent</TableHead>
                        <TableHead className="text-right">Already recv</TableHead>
                        <TableHead className="text-right">Open</TableHead>
                        <TableHead className="text-right w-28">Receive qty</TableHead>
                        <TableHead className="text-right w-24">Scrap</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receiveLines.map((line) => (
                        <TableRow key={line.key}>
                          <TableCell className="text-xs text-muted-foreground">
                            {line.sendChallanNo || `#${line.sendChallanId}`}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{line.itemLabel}</div>
                            {line.jobType ? (
                              <div className="text-xs text-muted-foreground">{line.jobType}</div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right">{formatQty(line.sentQty)}</TableCell>
                          <TableCell className="text-right">{formatQty(line.alreadyReceived)}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatQty(line.openQty)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={0}
                              max={line.openQty}
                              step="any"
                              className="h-8 text-right"
                              value={line.receiveQty}
                              onChange={(e) =>
                                updateReceiveLine(line.key, 'receiveQty', e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={0}
                              step="any"
                              className="h-8 text-right"
                              value={line.scrapQty}
                              onChange={(e) =>
                                updateReceiveLine(line.key, 'scrapQty', e.target.value)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={receiveSaving}
              onClick={() => setReceiveOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={receiveSaving || receiveLoading || receiveLines.length === 0}
              className="bg-primary-600 hover:bg-primary-700 text-white"
              onClick={() => void handleConfirmReceive()}
            >
              {receiveSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <PackageCheck className="h-4 w-4 mr-2" />
              )}
              Confirm receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default JobWorkOrderListingPage;
